
import { Attribute, Option, Step } from "zakeke-configurator-react/dist/declarations/composer/Module/src/models";
import { ReactComponent as AngleLeftSolid } from '../../../assets/icons/angle-left-solid.svg';
import { ReactComponent as AngleRightSolid } from '../../../assets/icons/angle-right-solid.svg';
import update from 'immutability-helper';
import savedCompositionsIcon from '../../../assets/icons/saved_designs.svg';
import star from '../../../assets/icons/star.svg';
import textIcon from '../../../assets/icons/font-solid.svg';
import OptionItem from '../../widgets/Option';
import Designer from '../Designer';

import { CarouselContainer, Icon } from 'components/Atomic';
import { getDefinitiveGroups, useUndoRedoActions, useUndoRegister } from 'Helpers';
import React, { useEffect, useState } from 'react';
import useStore from 'Store';
import styled from 'styled-components';
import { useZakeke } from 'zakeke-configurator-react';
import DesignsDraftList from '../DesignsDraftList';
import { ArrowIcon, AttributeAccordion, AttributeAccordionName, AttributeContainer, AttributeDescription, AttributeItem, AttributeName, AttributesContainer, GroupIcon, GroupItem, GroupsContainer, Options, OptionsContainer, OptionSelectedName, SelectorContainer } from '../LayoutStyled';
import Steps from '../Steps';
import arrowUp from '../../../assets/icons/angle-up-solid.svg';
import arrowDown from '../../../assets/icons/angle-down-solid.svg';

const SliderArrow = styled<React.FC<React.ComponentProps<typeof Icon> & { arrowDirection: 'left' | 'right' }>>(Icon)`
    background: white;
    border: 1px #EEE solid;
    border-radius: 3px;
    position: relative;

    ${props => props.arrowDirection === 'left' && `left: -28px`}
    ${props => props.arrowDirection === 'right' && `right: -28px`}
`;

const SelectorDesktop = () => {

    const designerStep: Step = {
        id: -2,
        guid: "0000-0000-0000-0003",
        name: "Designer step",
        displayOrder: 1,
        attributes: []
    }

    const { isSceneLoading, isAreaVisible, groups, product, templates, currentTemplate, setCamera, setTemplate, draftCompositions, sellerSettings } = useZakeke();
    const { setSelectedGroupId, selectedGroupId, setSelectedStepId, selectedStepId, setSelectedAttributeId, selectedAttributeId, isUndo, isRedo } = useStore();
    const [selectedCarouselSlide, setSelectedCarouselSlide] = useState<number>(0);
    const [attributesOpened, setAttributesOpened] = useState<{ attributeID: number, isOpened: boolean }[]>([]);
    const [isStartRegistering, setIsStartRegistering] = useState(false);

    const shouldCustomizerGroupBeVisible = !isSceneLoading && product ? product.areas.some(area => isAreaVisible(area.id)) : false;
    const hasDesignsSaved = (groups && draftCompositions && draftCompositions.length > 0) ?? false;

    const actualGroups = getDefinitiveGroups(groups, false, hasDesignsSaved, sellerSettings) ?? [];

    if (actualGroups.length > 0) {
        const indexStep = groups[0].steps[groups[0].steps.length - 1].name.toLowerCase().indexOf("custom") ? groups[0].steps.length - 2 : groups[0].steps.length - 1;
        const yesOption = groups[0].steps[indexStep].attributes[0].options.find(x => x.name.toLowerCase() === 'yes' && x.enabled);
        if (yesOption?.selected)
            actualGroups[0].steps.push(designerStep);
    }

    const selectedGroup = selectedGroupId ? actualGroups.find(group => group.id === selectedGroupId) : null;
    const selectedStep = selectedGroupId ? actualGroups.find(group => group.id === selectedGroupId)?.steps.find(step => step.id === selectedStepId) : null;
    const currentAttributes = selectedStep ? selectedStep.attributes : (selectedGroup ? selectedGroup.attributes : []);
    const selectedAttribute = currentAttributes ? currentAttributes.find(attr => attr.id === selectedAttributeId) : null;
    const undoRegistering = useUndoRegister();
    const undoRedoActions = useUndoRedoActions();
    const groupIndex = actualGroups && selectedGroup ? actualGroups.indexOf(selectedGroup) : 0;

    const handleNextStep = () => {
        if (selectedGroup) {
            if (groupIndex < actualGroups.length - 1) {
                const nextGroup = actualGroups[groupIndex + 1];
                handleGroupSelection(nextGroup.id);
            }
        }
    }

    const handlePreviousStep = () => {
        if (selectedGroup) {
            if (groupIndex > 0) {
                let previousGroup = actualGroups[groupIndex - 1];
                handleGroupSelection(previousGroup.id);

                // Select the last step
                if (previousGroup.steps.length > 0)
                    handleStepSelection(previousGroup.steps[previousGroup.steps.length - 1].id);
            }
        }
    }

    const handleStepChange = (step: Step | null) => {
        if (step)
            handleStepSelection(step.id)
    }

    const handleGroupSelection = (groupId: number) => {

        setIsStartRegistering(undoRegistering.startRegistering());

        if (groupId && selectedGroupId !== groupId && !isUndo && !isRedo) {
            undoRedoActions.eraseRedoStack();
            undoRedoActions.fillUndoStack({ type: "group", id: selectedGroupId, direction: "undo" });
            undoRedoActions.fillUndoStack({ type: "group", id: groupId ?? null, direction: "redo" });
        }

        setSelectedGroupId(groupId);
    }

    const handleStepSelection = (stepId: number | null) => {
        setIsStartRegistering(undoRegistering.startRegistering());

        if (selectedStepId !== stepId && !isUndo && !isRedo) {
            undoRedoActions.eraseRedoStack();
            undoRedoActions.fillUndoStack({ type: "step", id: selectedStepId, direction: "undo" });
            undoRedoActions.fillUndoStack({ type: "step", id: stepId ?? null, direction: "redo" });
        }
        setSelectedStepId(stepId);
    }

    const handleAttributeSelection = (attributeId: number, isAttributesVertical?: boolean) => {

        setIsStartRegistering(undoRegistering.startRegistering());

        if (attributeId && selectedAttributeId !== attributeId && !isUndo && !isRedo) {
            undoRedoActions.eraseRedoStack();
            undoRedoActions.fillUndoStack({ type: "attribute", id: selectedAttributeId, direction: "undo" });
            undoRedoActions.fillUndoStack({ type: "attribute", id: attributeId, direction: "redo" });
        }

        setSelectedAttributeId(attributeId);
        if (isAttributesVertical && !selectedGroup?.attributesAlwaysOpened) {
            const index = attributesOpened.findIndex(x => x.attributeID === attributeId);
            if (index === -1) {
                setAttributesOpened(prev => update(prev, {
                    $push: [{ attributeID: attributeId, isOpened: true }]
                }));
            }
            else {
                setAttributesOpened(prev => update(prev, {
                    [index]: {
                        'isOpened': {
                            $set: !prev[index].isOpened
                        }
                    }
                }));
            }
        }
    }

    // Initial template selection
    useEffect(() => {
        if (templates.length > 0 && !currentTemplate)
            setTemplate(templates[0].id);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templates]);


    // Initial group selection
    useEffect(() => {
        if (!isSceneLoading && actualGroups.length > 0 && !selectedGroupId) {
            handleGroupSelection(actualGroups[0].id);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSceneLoading, actualGroups]);

    // Reset attribute selection when group selection changes
    useEffect(() => {
        if (selectedGroup && selectedGroup.id !== -2) {
            if (selectedGroup.steps.length > 0) {
                handleStepSelection(selectedGroup.steps[0].id);
            }
            else {
                handleStepSelection(null);
            }
            if (selectedGroup.direction === 1) {
                if (!selectedGroup.attributesAlwaysOpened) {
                    let attributes = selectedGroup.attributes.length > 0 ? [selectedGroup.attributes[0]] : [];
                    attributes = attributes.concat(selectedGroup.steps.flatMap(s => s.attributes.length > 0 ? [s.attributes[0]] : []));

                    setAttributesOpened([...attributesOpened, ...attributes.map(attr => { return { attributeID: attr.id, isOpened: true } })]);
                }
                else {
                    const attributes = selectedGroup.attributes.concat(selectedGroup.steps.flatMap(s => s.attributes));
                    setAttributesOpened([...attributes.map(attr => { return { attributeID: attr.id, isOpened: true } })])
                }
            }

            setSelectedCarouselSlide(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupId]);


    // check at any changes if there are attributes that needs to be opened
    useEffect(() => {
        if (actualGroups) {
            actualGroups.forEach(group => {
                if (group.direction === 1) {
                    if (!group.attributesAlwaysOpened) {
                        let attributes: Attribute[] = [];
                        if (group.attributes.length > 0) {
                            if (!attributesOpened.some(x => x.attributeID === group.attributes[0].id))
                                attributes = [group.attributes[0]];
                        }
                        if (group.steps.length > 0) {
                            let stepsFirstAttributes = group.steps.map(s => { return s.attributes[0] });
                            stepsFirstAttributes.forEach(attribute => {
                                if (!attributesOpened.some(x => x.attributeID === attribute.id)) {
                                    attributes.push(attribute);
                                }
                            });

                        }
                        if (attributes.length > 0)
                            setAttributesOpened([...attributesOpened, ...attributes.map(attr => { return { attributeID: attr.id, isOpened: true } })]);
                    }
                    else {
                        const attributes = group.attributes.concat(group.steps.flatMap(s => s.attributes)).filter(x => !attributesOpened.some(attr => attr.attributeID === x.id));
                        if (attributes.length > 0)
                            setAttributesOpened([...attributes.map(attr => { return { attributeID: attr.id, isOpened: true } })])
                    }
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actualGroups]);


    // Camera
    useEffect(() => {
        if (!isSceneLoading && selectedGroup && selectedGroup.cameraLocationId) {
            setCamera(selectedGroup.cameraLocationId);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupId, isSceneLoading]);

    useEffect(() => {
        if (selectedStep && selectedStep.attributes.length > 0) {
            handleAttributeSelection(selectedStep.attributes[0].id);
        } else if (selectedGroup && selectedGroup.attributes.length > 0) {
            handleAttributeSelection(selectedGroup.attributes[0].id);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStepId, selectedGroupId]);

    useEffect(() => {
        if (isStartRegistering) {
            undoRegistering.endRegistering(false);
            setIsStartRegistering(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStartRegistering]);

    return <SelectorContainer>
        <GroupsContainer>
            {actualGroups.map(group => {
                if (group)
                    return <GroupItem
                        key={group.guid}
                        className={'group-item' + (group.id === selectedGroupId ? ' selected' : '')}
                        onClick={() => handleGroupSelection(group.id)}
                    >
                        <GroupIcon src={group.imageUrl && group.imageUrl !== "" && group.id !== -2 ? (group.id === -3 ? savedCompositionsIcon : group.imageUrl) : (group.id === -2 ? textIcon : star)} />
                        <span>{group.id === -1 ? "Configure" : group.name}</span>
                    </GroupItem>
                else
                    return null;
            })}
        </GroupsContainer>
        <AttributesContainer>
            {/* Steps */}
            {selectedGroup && selectedGroupId !== -2 && selectedGroup.steps && selectedGroup.steps.length > 0 && <Steps
                key={"steps-" + selectedGroupId}
                hasNextGroup={groupIndex !== actualGroups.length - 1}
                hasPreviousGroup={groupIndex !== 0}
                onNextStep={handleNextStep}
                onPreviousStep={handlePreviousStep}
                currentStep={selectedStep}
                steps={selectedGroup.steps}
                onStepChange={handleStepChange}
            />}

            {selectedGroupId && selectedGroupId !== -2 && selectedGroupId !== -3 &&
                <>
                    {/* Attributes */}
                    {selectedGroup?.direction === 0 && <>
                        <CarouselContainer
                            key={selectedGroupId}
                            slidesToShow={window.innerWidth <= 1600 ? 3 : 4}
                            slideIndex={selectedCarouselSlide}
                            afterSlide={setSelectedCarouselSlide}
                            slidesToScroll={1}
                            speed={50}
                            renderBottomCenterControls={() => <span />}

                            renderCenterRightControls={({ nextSlide, currentSlide, slideCount }) =>
                                (currentSlide + (window.innerWidth <= 1600 ? 3 : 4) > slideCount - 1 ? null : <SliderArrow arrowDirection='right' onClick={nextSlide}><AngleRightSolid /></SliderArrow>)}

                            renderCenterLeftControls={({ previousSlide, currentSlide, slideCount }) =>
                                (currentSlide === 0 ? null : <SliderArrow arrowDirection='left' onClick={previousSlide}><AngleLeftSolid /></SliderArrow>)}>

                            {currentAttributes && currentAttributes.map(attribute => {
                                return <AttributeItem selected={attribute.id === selectedAttributeId} key={attribute.guid} onClick={() => handleAttributeSelection(attribute.id)}>
                                    <AttributeName > {attribute.name.toUpperCase()} </AttributeName>
                                    <OptionSelectedName>{attribute.options.find(opt => opt.selected)?.name ?? ""}</OptionSelectedName>
                                </AttributeItem>
                            })}
                        </CarouselContainer>
                        {/* Options */}
                        <OptionsContainer key={"options-container"}>
                            <Options key={"option"}>
                                {selectedAttribute && selectedAttribute.options.map(option => option.enabled && <OptionItem
                                    key={option.guid}
                                    selectedOptionId={selectedAttribute.options.find(opt => opt.selected)!.id}
                                    option={option} />)}
                            </Options>
                        </OptionsContainer>
                        <AttributeDescription key={"attribute-description"}>{selectedAttribute?.description}</AttributeDescription>
                    </>
                    }
                    {selectedGroup?.direction === 1 &&
                        <>
                            {currentAttributes && currentAttributes.map(attribute => {
                                return <AttributeContainer key={"container" + attribute.code}>
                                    <AttributeAccordion
                                        key={"accordion " + attribute.guid}
                                        opened={attributesOpened.find(attr => attr.attributeID === attribute.id)?.isOpened}
                                        onClick={() => handleAttributeSelection(attribute.id, true)} >
                                        <AttributeAccordionName key={attribute.name}>{attribute.name}</AttributeAccordionName>
                                        {!selectedGroup.attributesAlwaysOpened && <ArrowIcon
                                            key={"accordion-icon"}
                                            src={attributesOpened.find(attr => attr.attributeID === attribute.id)?.isOpened ? arrowUp : arrowDown} />}
                                    </AttributeAccordion>
                                    {attributesOpened && attributesOpened.find(attr => attr.attributeID === attribute.id)?.isOpened &&
                                        <OptionsContainer>
                                            <Options>
                                                {attribute.options.map(option => option.enabled && <OptionItem
                                                    key={option.guid}
                                                    selectedOptionId={attribute.options.find(opt => opt.selected)!.id}
                                                    option={option} />)}
                                            </Options>
                                        </OptionsContainer>
                                    }
                                </AttributeContainer>
                            })}
                        </>
                    }
                </>
            }

            {/* Designer / Customizer */}
            {selectedStepId === -2 && <Designer />}

            {/* Saved Compositions */}
            {draftCompositions && selectedGroupId === -3 && <DesignsDraftList />}

        </AttributesContainer>
    </SelectorContainer >
}

export default SelectorDesktop;